import React, { useState } from 'react';
import { TransactionUpdateDto } from '@eagerdog/interfaces';
import Button from 'src/components/Button/Button';
import Checkbox from 'src/components/Checkbox/Checkbox';
import Dropdown, { IOption } from 'src/components/Dropdown/Dropdown';
import Input from 'src/components/Input/Input';
import { apiService } from 'src/services/api.service';
import { toast } from 'src/components/Toast/ToastManager';
import styles from './EditTransaction.module.scss';
import { Constants } from '@eagerdog/constants';

interface EditTransactionProps {
  transaction: any;
  eventId: string;
  onFinish: () => void;
  onCancel: () => void;
  // 'request' renders the "Send Payment Request" (online, card) layout,
  // 'edit' renders the "Edit Transaction" (offline, direct override) layout.
  // Defaults based on the transaction's payment method for backwards compatibility.
  mode?: 'edit' | 'request';
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  [Constants.payment_method_type.card]: 'Card (Online)',
  [Constants.payment_method_type.bank_account]: 'Bank Account (ACH)',
  [Constants.payment_method_type.cash]: 'Cash',
  [Constants.payment_method_type.cheque]: 'Cheque',
  [Constants.payment_method_type.etransfer]: 'E-Transfer',
  [Constants.payment_method_type.paypal]: 'PayPal',
  [Constants.payment_method_type.other]: 'Other',
};

const EditTransaction: React.FC<EditTransactionProps> = ({
  transaction,
  eventId,
  onFinish,
  onCancel,
  mode,
}) => {
  const isCard =
    transaction.amounts?.payment_method_type === Constants.payment_method_type.card;
  const isBankAccount =
    transaction.amounts?.payment_method_type === Constants.payment_method_type.bank_account;
  const effectiveMode: 'edit' | 'request' = mode ?? (isCard ? 'request' : 'edit');
  const originalPaymentMethodLabel =
    PAYMENT_METHOD_LABELS[transaction.amounts?.payment_method_type] ||
    transaction.amounts?.payment_method_type ||
    'Unknown';

  const currentTotalCents: number =
    transaction.amounts?.calculated_base_amount ?? 0;
  const currentTotalDollars = currentTotalCents / 100;

  const [loading, setLoading] = useState(false);

  // --- offline-edit state ---
  const [paymentMethodType, setPaymentMethodType] = useState(
    transaction.amounts?.payment_method_type || ''
  );
  const [chequeNumber, setChequeNumber] = useState(
    transaction.amounts?.cheque_number || ''
  );
  const [totalAmount, setTotalAmount] = useState<string>(() => {
    const base = transaction.amounts?.calculated_base_amount || 0;
    const discount = transaction.amounts?.calculated_discount_amount || 0;
    const total = (base - discount) / 100;
    return total > 0 ? String(total) : '';
  });
  const [overrideNote, setOverrideNote] = useState<string>(
    transaction.override_note || ''
  );

  // --- card / payment-request state ---
  const [newTotal, setNewTotal] = useState<string>('');
  const [requestNote, setRequestNote] = useState<string>('');
  // Nothing was actually collected for card/bank_account originals (they're settled
  // via Stripe already), so default to a delta on top. Other offline methods
  // (cash/cheque/e-transfer/paypal/other) are often used as placeholders for entries
  // that haven't actually been paid yet, so default to requesting the full amount.
  const [requestFullAmount, setRequestFullAmount] = useState<boolean>(
    !isCard && !isBankAccount
  );

  const paymentMethods: IOption[] = [
    { value: 'Cheque', id: Constants.payment_method_type.cheque },
    { value: 'Cash', id: Constants.payment_method_type.cash },
    { value: 'E-Transfer', id: Constants.payment_method_type.etransfer },
    { value: 'Paypal', id: Constants.payment_method_type.paypal },
    { value: 'Other', id: Constants.payment_method_type.other },
  ];

  // --- card submit ---
  const handleCardSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const parsedNew = parseFloat(newTotal);
    if (requestFullAmount) {
      if (isNaN(parsedNew) || parsedNew <= 0) {
        toast.show({
          title: 'Error',
          content: 'Please enter a valid amount to request',
          type: 'fail',
          duration: 5000,
        });
        return;
      }
    } else if (isNaN(parsedNew) || parsedNew <= currentTotalDollars) {
      toast.show({
        title: 'Error',
        content: `New entry total must be greater than the current entry amount ($${currentTotalDollars.toFixed(2)})`,
        type: 'fail',
        duration: 5000,
      });
      return;
    }

    setLoading(true);
    try {
      await apiService.createPaymentRequest(eventId, transaction._id, {
        new_total: Math.round(parsedNew * 100),
        note: requestNote.trim() || undefined,
        full_amount: requestFullAmount,
      });
      toast.show({
        title: 'Payment request sent',
        content: 'The registrant will receive an email with a payment link.',
        type: 'success',
        duration: 5000,
      });
      onFinish();
    } catch (error: any) {
      toast.show({
        title: 'Error',
        content: error.response?.data?.message || 'Failed to send payment request',
        type: 'fail',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  // --- offline submit ---
  const handleOfflineSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!paymentMethodType) {
      toast.show({
        title: 'Error',
        content: 'Please select a payment method',
        type: 'fail',
        duration: 5000,
      });
      return;
    }

    if (
      paymentMethodType === Constants.payment_method_type.cheque &&
      !chequeNumber.trim()
    ) {
      toast.show({
        title: 'Error',
        content: 'Please enter a cheque number',
        type: 'fail',
        duration: 5000,
      });
      return;
    }

    const parsedAmount = parseFloat(totalAmount);
    if (totalAmount !== '' && (isNaN(parsedAmount) || parsedAmount < 0)) {
      toast.show({
        title: 'Error',
        content: 'Please enter a valid amount',
        type: 'fail',
        duration: 5000,
      });
      return;
    }

    setLoading(true);
    try {
      const updateData: TransactionUpdateDto = { payment_method_type: paymentMethodType };

      if (paymentMethodType === Constants.payment_method_type.cheque) {
        updateData.cheque_number = chequeNumber.trim();
      }

      if (totalAmount !== '') {
        updateData.total_amount = Math.round(parsedAmount * 100);
      }

      updateData.override_note = overrideNote.trim();

      await apiService.updateEventTransaction(eventId, transaction._id, updateData);
      toast.show({
        title: 'Success',
        content: 'Transaction updated successfully',
        type: 'success',
        duration: 5000,
      });
      onFinish();
    } catch (error: any) {
      toast.show({
        title: 'Error',
        content: error.response?.data?.message || 'Failed to update transaction',
        type: 'fail',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  // ----------- PAYMENT REQUEST (ONLINE, CARD) LAYOUT -----------
  if (effectiveMode === 'request') {
    return (
      <form className={styles.editTransactionModal} onSubmit={handleCardSubmit}>
        <div className={styles.title}>Send Payment Request</div>

        <div className={styles.modalInner}>
          <div className={styles.infoBanner}>
            {isCard ? (
              <>
                Increasing the price will send a payment request email to the registrant.
                The supplemental transaction will remain in <strong>Awaiting Payment</strong> status
                until the balance is collected.
              </>
            ) : (
              <>
                Original payment method: <strong>{originalPaymentMethodLabel}</strong>. The
                additional amount below will be collected online by card. The supplemental
                transaction will remain in <strong>Awaiting Payment</strong> status until the
                balance is collected.
              </>
            )}
          </div>

        <div className={styles.metaRow}>
          <div className={styles.metaField}>
            <label className={styles.readOnlyLabel}>Payment Method</label>
            <div className={styles.readOnly}>{originalPaymentMethodLabel}</div>
          </div>
          <div className={styles.metaField}>
            <label className={styles.readOnlyLabel}>Current Entry Amount</label>
            <div className={styles.readOnly}>${currentTotalDollars.toFixed(2)}</div>
          </div>
        </div>

          <div className={styles.formField}>
            <Checkbox
              id="requestFullAmount"
              label="Nothing has been collected for this transaction yet"
              value={requestFullAmount}
              onChange={() => {
                const next = !requestFullAmount;
                setRequestFullAmount(next);
                if (next && !newTotal) {
                  setNewTotal(String(currentTotalDollars.toFixed(2)));
                }
              }}
            />
          </div>

          <div className={styles.formField}>
            <Input
              label={
                requestFullAmount
                  ? 'Amount To Request (full amount) — fees will be added on top'
                  : `New Entry Total (must be > $${currentTotalDollars.toFixed(2)}) — fees will be added on top`
              }
              type="number"
              value={newTotal}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setNewTotal(e.target.value)
              }
              placeholder={
                requestFullAmount
                  ? String(currentTotalDollars.toFixed(2))
                  : String((currentTotalDollars + 0.01).toFixed(2))
              }
            />
          </div>

          <div className={styles.formField}>
            <label className={styles.noteLabel}>Note to registrant (optional)</label>
            <textarea
              className={styles.noteTextarea}
              value={requestNote}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setRequestNote(e.target.value)
              }
              placeholder="Reason for the additional payment..."
              rows={3}
            />
          </div>
        </div>

        <div className={styles.modalActions}>
          <Button type="button" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Sending...' : 'Send Payment Request'}
          </Button>
        </div>
      </form>
    );
  }

  // ----------- OFFLINE LAYOUT -----------
  return (
    <form className={styles.editTransactionModal} onSubmit={handleOfflineSubmit}>
      <div className={styles.title}>Edit Transaction</div>

      <div className={styles.modalInner}>
        <div className={styles.formField}>
          <Dropdown
            label="Payment Method"
            options={paymentMethods}
            value={
              paymentMethods.find((m) => m.id === paymentMethodType) ||
              paymentMethods[0]
            }
            onChange={(e: any, value: IOption) => {
              setPaymentMethodType(value.id);
              if (value.id !== 'cheque') {
                setChequeNumber('');
              }
            }}
            placeholder="Select payment method"
          />
        </div>

        {paymentMethodType === Constants.payment_method_type.cheque && (
          <div className={`${styles.formField} ${styles.chequeField}`}>
            <Input
              label="Cheque Number"
              type="text"
              value={chequeNumber}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setChequeNumber(e.target.value)
              }
              placeholder="Enter cheque number"
            />
          </div>
        )}

        <div className={styles.formField}>
          <Input
            label="Total Amount"
            type="number"
            value={totalAmount}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setTotalAmount(e.target.value)
            }
            placeholder="0.00"
          />
        </div>

        <div className={styles.formField}>
          <label className={styles.noteLabel}>Override Note (optional)</label>
          <textarea
            className={styles.noteTextarea}
            value={overrideNote}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setOverrideNote(e.target.value)
            }
            placeholder="Reason for price adjustment..."
            rows={3}
          />
        </div>
      </div>

      <div className={styles.modalActions}>
        <Button type="button" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? 'Updating...' : 'Update Transaction'}
        </Button>
      </div>
    </form>
  );
};

export default EditTransaction;
